import type {
  AdminEventRequest,
  EventOption,
  EventOptions,
} from "../types/adminEvent";

export const eventTypes = [
  "birthday",
  "nursery_event",
  "graduation",
  "holiday",
  "field_trip",
  "special_event",
] as const;
export const mediaAccept = ".jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.webm";
export const captureFields = [
  "date",
  "classroom_id",
  "child_id",
  "camera_id",
  "start_time",
  "end_time",
] as const;
export const emptyEvent = (): AdminEventRequest => ({
  title: "",
  type: "nursery_event",
  date: "",
  teacher_id: null,
});
export const localDate = (value: string) => value.slice(0, 10);
export const localTime = (value?: string | null) => value?.slice(0, 5) || null;

// These records came from nursery-scoped endpoints. Reject explicit mismatches too.
export const nurseryOptions = (records: EventOption[], nurseryId: string) =>
  records.filter(
    (record) =>
      record.nursery_id == null || String(record.nursery_id) === nurseryId,
  );
export const classroomOptions = (
  records: EventOption[],
  classroomId?: number | null,
) =>
  records.filter(
    (record) => !classroomId || Number(record.classroom_id) === classroomId,
  );
export const changeClassroom = (
  form: AdminEventRequest,
  classroomId: number | null,
  options: EventOptions,
): AdminEventRequest => ({
  ...form,
  classroom_id: classroomId,
  child_id: classroomOptions(options.children, classroomId).some(
    (record) => record.id === form.child_id,
  )
    ? form.child_id
    : null,
  camera_id: classroomOptions(options.cameras, classroomId).some(
    (record) => record.id === form.camera_id,
  )
    ? form.camera_id
    : null,
});

export const validateEvent = (
  form: AdminEventRequest,
  options: EventOptions,
  _editing = false,
  t?: (key: string, params?: Record<string, string>) => string,
): string | null => {
  const v = (key: string, params?: Record<string, string>) =>
    t ? t(key, params) : key;
  if (!form.title.trim() || form.title.length > 255)
    return v("validation.invalidTitle");
  if (!eventTypes.includes(form.type))
    return v("validation.invalidType");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date))
    return v("validation.invalidDate");
  if (form.teacher_id == null)
    return v("validation.teacherRequired");
  const [year, month, day] = form.date.split("-").map(Number);
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  if (
    !year ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day >
      [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]
  )
    return v("validation.invalidCalendarDate");
  for (const time of [form.start_time, form.end_time]) {
    if (time && !/^([01]\d|2[0-3]):[0-5]\d$/.test(time))
      return v("validation.invalidTime");
  }
  if (form.end_time && (!form.start_time || form.end_time <= form.start_time))
    return v("validation.endTimeBeforeStart");
  for (const [key, records] of [
    ["teacher_id", options.teachers],
    ["classroom_id", options.classrooms],
    ["child_id", classroomOptions(options.children, form.classroom_id)],
    ["camera_id", classroomOptions(options.cameras, form.classroom_id)],
  ] as const) {
    if (form[key] != null && !records.some((record) => record.id === form[key]))
      return v("validation.unavailableOption", {
        field: key.replace("_id", ""),
      });
  }
  return null;
};

// Explicit whitelist: nursery_id is never allowed in a request body. Undefined
// ownership is preserved on update; null deliberately clears it.
export const eventPayload = (
  form: Partial<AdminEventRequest>,
): Partial<AdminEventRequest> => {
  const payload: Partial<AdminEventRequest> = {};
  for (const key of [
    "title",
    "type",
    ...captureFields,
    "teacher_id",
  ] as const) {
    if (form[key] !== undefined) Object.assign(payload, { [key]: form[key] });
  }
  if (payload.start_time === null || payload.start_time === "") {
    payload.start_time = null;
    payload.end_time = null;
  }
  if (payload.end_time && !payload.start_time)
    throw new Error("Include start time when submitting end time.");
  return payload;
};

export const validateMedia = (
  files: Pick<File, "name" | "size">[],
): string | null => {
  for (const file of files) {
    if (!/\.(jpe?g|png|gif|webp|mp4|mov|avi|webm)$/i.test(file.name))
      return `${file.name}: unsupported file extension.`;
    if (file.size > 50 * 1024 * 1024)
      return `${file.name}: maximum file size is 50MB.`;
  }
  return null;
};

export const eventError = (error: unknown): string => {
  const err = error as {
    response?: {
      status?: number;
      data?: { errors?: Record<string, string[]>; message?: string };
    };
    message?: string;
  };
  const data = err?.response?.data;
  if (data?.errors)
    return Object.entries(data.errors)
      .map(([field, messages]) => `${field}: ${[messages].flat().join(" ")}`)
      .join("\n");
  if (err?.response?.status === 403)
    return (
      data?.message || "You do not have permission to perform this action."
    );
  if (err?.response?.status === 404)
    return (
      data?.message ||
      "This event or media is no longer available. Refresh the list."
    );
  return (
    data?.message ||
    err?.message ||
    "Unable to complete the request. Please try again."
  );
};
